import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpDown, Search } from "lucide-react";
import { format, parseISO } from "date-fns";
import { AppHeader } from "@/components/app/AppHeader";
import { Input } from "@/components/ui/input";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import { getUserReport } from "@/lib/rpc/user-report.functions";

export const Route = createFileRoute("/_app/_team/team/users/$id")({
  component: UserReportPage,
  head: () => ({ meta: [{ title: "User report — Team" }] }),
});

type SortDir = "asc" | "desc";

function useSort<T>(rows: T[], initial: keyof T) {
  const [key, setKey] = useState<keyof T>(initial);
  const [dir, setDir] = useState<SortDir>("desc");
  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = a[key] as unknown as string | number | null;
      const bv = b[key] as unknown as string | number | null;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, key, dir]);
  const toggle = (k: keyof T) => {
    if (k === key) setDir(dir === "asc" ? "desc" : "asc");
    else { setKey(k); setDir("desc"); }
  };
  return { sorted, key, dir, toggle };
}

function SortHead<T>({ label, k, sort }: { label: string; k: keyof T; sort: ReturnType<typeof useSort<T>> }) {
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => sort.toggle(k)}
        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground hover:text-foreground"
      >
        {label}
        <ArrowUpDown className="h-3 w-3 opacity-60" />
        {sort.key === k && <span className="text-[8px]">{sort.dir === "asc" ? "▲" : "▼"}</span>}
      </button>
    </TableHead>
  );
}

function UserReportPage() {
  const { id } = Route.useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["user-report", id],
    queryFn: () => getUserReport({ data: { user_id: id } }),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <>
        <AppHeader subtitle="User report" />
        <div className="px-[22px] pb-6 text-[12px] text-muted-foreground">Loading…</div>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <AppHeader subtitle="User report" />
        <div className="px-[22px] pb-6 text-[12px] text-destructive">
          {(error as Error | null)?.message ?? "Failed to load."}
        </div>
      </>
    );
  }

  const { profile, roles, submissions, otherTasks, documents } = data;

  // KPIs
  const totalSubs = submissions.length;
  const totalPoints = submissions.reduce((s, r) => s + (r.points ?? 0), 0);
  const corrections = submissions.filter((r) => r.correction_status === "fix").length;
  const completed = submissions.filter((r) => r.status === "completed" || r.status === "approved").length;
  const approvalRate = totalSubs > 0 ? Math.round((completed / totalSubs) * 100) : 0;
  const minutes =
    submissions.reduce((s, r) => s + (r.duration_minutes ?? 0), 0) +
    otherTasks.reduce((s, r) => {
      if (!r.start_time || !r.end_time) return s;
      const [sh, sm] = r.start_time.split(":").map(Number);
      const [eh, em] = r.end_time.split(":").map(Number);
      return s + (eh * 60 + em - (sh * 60 + sm));
    }, 0);
  const hoursLogged = (minutes / 60).toFixed(1);

  // Activity feed: last 30 chronologically (mix subs + other)
  const feed = [
    ...submissions.map((s) => ({
      kind: "submission" as const,
      id: s.id,
      title: s.title,
      meta: `${s.table.replace(/_/g, " ")} · ${s.status ?? "—"}`,
      ts: s.created_at,
    })),
    ...otherTasks.map((t) => ({
      kind: "other" as const,
      id: t.id,
      title: t.task_type,
      meta: `${t.table.replace(/_/g, " ")} · ${t.notes.slice(0, 60)}`,
      ts: t.created_at,
    })),
  ]
    .sort((a, b) => (a.ts < b.ts ? 1 : -1))
    .slice(0, 30);

  return (
    <>
      <AppHeader subtitle={`User report · ${profile.full_name}`} />
      <div className="space-y-4 px-4 pb-6 sm:px-[22px]">
        <Link
          to="/team/employees"
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Back to employees
        </Link>

        {/* Profile header */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-lg font-bold">
              {profile.full_name?.[0]?.toUpperCase() ?? "?"}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-[18px] font-bold">{profile.full_name}</h2>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                <span>{profile.email}</span>
                <span>·</span>
                <span>
                  {DEPARTMENT_LABELS[profile.department as Department] ?? profile.department}
                </span>
                <span>·</span>
                <span className="font-mono">#{profile.employee_id ?? "—"}</span>
                <span>·</span>
                <span className="capitalize">{roles.join(", ") || "—"}</span>
                <span>·</span>
                <span className="rounded-full bg-muted px-2 py-0.5 capitalize">{profile.status}</span>
              </div>
            </div>
          </div>
        </div>

        {(() => {
          const isViewedEditor = profile.department === "editor";
          return (
            <div className={`grid grid-cols-2 gap-3 ${isViewedEditor ? "md:grid-cols-5" : "md:grid-cols-4"}`}>
              <KpiCard label="Total Submissions" value={String(totalSubs)} />
              {isViewedEditor && <KpiCard label="Total Points" value={String(totalPoints)} />}
              <KpiCard label="Corrections" value={String(corrections)} accent="warn" />
              <KpiCard label="Approval Rate" value={`${approvalRate}%`} accent="ok" />
              <KpiCard label="Hours Logged" value={hoursLogged} />
            </div>
          );
        })()}

        <Tabs defaultValue="submissions">
          <TabsList className="flex flex-wrap">
            <TabsTrigger value="submissions">All Submissions ({submissions.length})</TabsTrigger>
            <TabsTrigger value="other">Other Tasks ({otherTasks.length})</TabsTrigger>
            <TabsTrigger value="corrections">Corrections ({corrections})</TabsTrigger>
            <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
            <TabsTrigger value="activity">Activity Feed</TabsTrigger>
          </TabsList>

          <TabsContent value="submissions" className="mt-4">
            <SubmissionsTable rows={submissions} isEditor={profile.department === "editor"} />
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            <OtherTasksTable rows={otherTasks} />
          </TabsContent>
          <TabsContent value="corrections" className="mt-4">
            <SubmissionsTable rows={submissions.filter((s) => s.correction_status === "fix")} showNote isEditor={profile.department === "editor"} />
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            <DocumentsTable rows={documents} />
          </TabsContent>
          <TabsContent value="activity" className="mt-4">
            <div className="rounded-lg border border-border bg-card shadow-sm">
              <ul className="divide-y divide-border">
                {feed.length === 0 && (
                  <li className="px-4 py-6 text-center text-[12px] text-muted-foreground">
                    No activity yet.
                  </li>
                )}
                {feed.map((f) => (
                  <li key={`${f.kind}-${f.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <div className="min-w-0">
                      <div className="truncate text-[12px] font-semibold">{f.title}</div>
                      <div className="truncate text-[10px] text-muted-foreground">{f.meta}</div>
                    </div>
                    <span className="shrink-0 text-[10px] text-muted-foreground">
                      {f.ts ? format(parseISO(f.ts), "dd MMM HH:mm") : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function KpiCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: "ok" | "warn";
}) {
  const tone =
    accent === "warn"
      ? "text-amber-600"
      : accent === "ok"
        ? "text-emerald-600"
        : "text-foreground";
  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-sm">
      <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 text-[20px] font-bold ${tone}`}>{value}</div>
    </div>
  );
}

interface SubRow {
  id: string;
  table: string;
  title: string;
  date: string;
  status: string | null;
  points: number;
  correction_status: string | null;
  correction_note: string | null;
  duration_minutes: number | null;
  created_at: string;
}

function SubmissionsTable({ rows, showNote = false, isEditor = false }: { rows: SubRow[]; showNote?: boolean; isEditor?: boolean }) {
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusF !== "all" && (r.status ?? "") !== statusF) return false;
      if (!term) return true;
      return (
        r.title.toLowerCase().includes(term) ||
        r.table.toLowerCase().includes(term) ||
        (r.status ?? "").toLowerCase().includes(term)
      );
    });
  }, [rows, q, statusF]);
  const sort = useSort<SubRow>(filtered, "date");

  const statuses = Array.from(new Set(rows.map((r) => r.status).filter(Boolean) as string[]));

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter…"
            className="h-8 pl-7 text-[12px]"
          />
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="h-8 w-[160px] text-[12px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((s) => (
              <SelectItem key={s} value={s} className="capitalize">
                {s.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-muted-foreground">{filtered.length} rows</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <SortHead<SubRow> label="Date" k="date" sort={sort} />
            <SortHead<SubRow> label="Title" k="title" sort={sort} />
            <SortHead<SubRow> label="Source" k="table" sort={sort} />
            <SortHead<SubRow> label="Status" k="status" sort={sort} />
            {isEditor && <SortHead<SubRow> label="Pts" k="points" sort={sort} />}
            <SortHead<SubRow> label="Mins" k="duration_minutes" sort={sort} />
            {showNote && <TableHead>Note</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sort.sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={(isEditor ? 6 : 5) + (showNote ? 1 : 0)} className="text-center text-[12px] text-muted-foreground">
                No records.
              </TableCell>
            </TableRow>
          )}
          {sort.sorted.map((r) => (
            <TableRow key={`${r.table}-${r.id}`}>
              <TableCell className="text-[12px]">{r.date}</TableCell>
              <TableCell className="text-[12px] font-medium">{r.title}</TableCell>
              <TableCell className="text-[11px] text-muted-foreground">
                {r.table.replace(/_/g, " ")}
              </TableCell>
              <TableCell className="text-[12px] capitalize">
                {(r.status ?? "—").replace(/_/g, " ")}
              </TableCell>
              {isEditor && <TableCell className="text-[12px] font-semibold">{r.points}</TableCell>}
              <TableCell className="text-[12px]">{r.duration_minutes ?? "—"}</TableCell>
              {showNote && (
                <TableCell className="max-w-[260px] truncate text-[11px] text-muted-foreground">
                  {r.correction_note ?? "—"}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface OtherRow {
  id: string;
  table: string;
  task_type: string;
  notes: string;
  date: string;
  start_time: string | null;
  end_time: string | null;
  created_at: string;
}

function OtherTasksTable({ rows }: { rows: OtherRow[] }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        r.task_type.toLowerCase().includes(term) || r.notes.toLowerCase().includes(term),
    );
  }, [rows, q]);
  const sort = useSort<OtherRow>(filtered, "date");

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex items-center gap-2 border-b border-border p-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter…"
            className="h-8 pl-7 text-[12px]"
          />
        </div>
        <span className="text-[11px] text-muted-foreground">{filtered.length} rows</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <SortHead<OtherRow> label="Date" k="date" sort={sort} />
            <SortHead<OtherRow> label="Task" k="task_type" sort={sort} />
            <TableHead>Notes</TableHead>
            <TableHead>Time</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sort.sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-center text-[12px] text-muted-foreground">
                No tasks.
              </TableCell>
            </TableRow>
          )}
          {sort.sorted.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="text-[12px]">{r.date}</TableCell>
              <TableCell className="text-[12px] font-medium capitalize">
                {r.task_type.replace(/_/g, " ")}
              </TableCell>
              <TableCell className="max-w-[320px] truncate text-[11px] text-muted-foreground">
                {r.notes}
              </TableCell>
              <TableCell className="text-[11px] text-muted-foreground">
                {r.start_time && r.end_time ? `${r.start_time}–${r.end_time}` : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface DocRow {
  id: string;
  title: string;
  doc_type: string;
  created_at: string;
  file_path: string;
}

function DocumentsTable({ rows }: { rows: DocRow[] }) {
  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Uploaded</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="text-center text-[12px] text-muted-foreground">
                No documents.
              </TableCell>
            </TableRow>
          )}
          {rows.map((d) => (
            <TableRow key={d.id}>
              <TableCell className="text-[12px] font-medium">{d.title}</TableCell>
              <TableCell className="text-[12px] capitalize">{d.doc_type.replace(/_/g, " ")}</TableCell>
              <TableCell className="text-[11px] text-muted-foreground">
                {d.created_at?.slice(0, 10)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
